import Sparkle from "@/assets/svg/AI-Summary.svg?react";
import IconButton from "@/components/ui/icon-button";
import {
  Menu,
  MenuItem,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/ui/menu";
import { CustomMenuItem, type MenuItemConfig } from "@/components/ui/menu-item";
import { useFeature } from "@/lib/feature-management";
import useFeedContentManagementStore from "@/store/news-feed/feed-content-management/feed-content-management.slice";
import type { DynamicFilterItem } from "@/store/news-feed/filters/dynamic-filters.slice";
import useNewsFeedFilters from "@/store/news-feed/filters/newsFeed-filter.slice";
import { useSelectedNarrativeClusters } from "@/store/news-feed/params/newsFeed-params.selectors";
import {
  DotsThreeIcon,
  IntersectIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { Fragment } from "react";

/**
 * Bulk action menu for narrative clusters
 * Provides actions like merge clusters
 */
const NarrativeClusterBulkActionMenu = () => {
  const setBulkMergeAndDeleteModalOpen = useNewsFeedFilters(
    (state) => state.setBulkMergeAndDeleteModalOpen,
  );
  const setSelectedClusterIds = useNewsFeedFilters(
    (state) => state.setSelectedClusterIds,
  );
  const selectedNarrativeClusters = useSelectedNarrativeClusters();

  const handleMergeClusters = () => {
    if (selectedNarrativeClusters.length > 0) {
      const clusterIds = new Set(
        selectedNarrativeClusters.map((cluster) => cluster.id),
      );
      setSelectedClusterIds(clusterIds);
    }
    setBulkMergeAndDeleteModalOpen(true, "merge");
  };

  const handleDeleteClusters = () => {
    if (selectedNarrativeClusters.length > 0) {
      const clusterIds = new Set(
        selectedNarrativeClusters.map((cluster) => cluster.id),
      );
      setSelectedClusterIds(clusterIds);
    }
    setBulkMergeAndDeleteModalOpen(true, "delete");
  };

  return (
    <MenuTrigger>
      <IconButton
        icon={<DotsThreeIcon className="w-6" />}
        aria-label="Narrative Cluster More Actions"
        variant="ghost"
        size="sm"
        tooltipLabel="Narrative Cluster More Actions"
        className="p-0"
      />
      <MenuPopover>
        <Menu>
          <MenuItem className="text-xs" onAction={handleMergeClusters}>
            <IntersectIcon className="text-gray-600" />
            Merge clusters
          </MenuItem>
          <MenuItem className="text-xs" onAction={handleDeleteClusters}>
            <TrashIcon className="text-red-750" />
            Delete clusters
          </MenuItem>
        </Menu>
      </MenuPopover>
    </MenuTrigger>
  );
};

interface NarrativeClusterFilterListItemMenuProps {
  item: DynamicFilterItem;
  setNarrativeClusterDeleteModalOpen: (isOpen: boolean) => void;
  setRenameClusterModalOpen: (isOpen: boolean) => void;
  setSelectedCluster: (
    cluster: {
      id: string;
      name: string;
    } | null,
  ) => void;
}

/**
 * Menu for individual narrative cluster filter items
 * Provides actions like generate insights, rename, and delete
 */
const NarrativeClusterFilterListItemMenu = ({
  item,
  setNarrativeClusterDeleteModalOpen,
  setRenameClusterModalOpen,
  setSelectedCluster,
}: NarrativeClusterFilterListItemMenuProps) => {
  const showNarrativeIntelV2 = useFeature("narrative-intel-v2", true);
  const showLlmPerception = useFeature("llm-perception-enabled", false);

  const setInsightsNarrativeClusterId = useFeedContentManagementStore(
    (state) => state.setInsightsNarrativeClusterId,
  );
  const setNarrativeIntelligenceV2ModalOpen = useFeedContentManagementStore(
    (state) => state.setNarrativeIntelligenceV2ModalOpen,
  );
  const setLlmPerceptionModalOpen = useNewsFeedFilters(
    (state) => state.setLlmPerceptionModalOpen,
  );
  const setLlmPerceptionClusterId = useNewsFeedFilters(
    (state) => state.setLlmPerceptionClusterId,
  );

  if (!item.id) return null;

  const menuItems: MenuItemConfig[] = [
    {
      id: "generate-narrative-intelligence",
      icon: <Sparkle className="text-violet-800" width={14} height={14} />,
      label: (
        <div className="flex gap-2 items-center">
          <span className="text-violet-800">
            Generate Narrative Intelligence
          </span>
        </div>
      ),
      onClick: () => {
        if (!item.id) return;
        setInsightsNarrativeClusterId(item.id);
        setNarrativeIntelligenceV2ModalOpen(true);
      },
      condition: showNarrativeIntelV2,
    },
    {
      id: "generate-llm-perception",
      icon: <Sparkle className="text-violet-800" width={14} height={14} />,
      label: (
        <div className="flex gap-2 items-center">
          <span className="text-violet-800">Generate LLM Perception</span>
        </div>
      ),
      onClick: () => {
        if (!item.id) return;
        setLlmPerceptionClusterId(item.id);
        setLlmPerceptionModalOpen(true);
      },
      condition: showLlmPerception,
    },
    {
      id: "rename-cluster",
      icon: <PencilSimpleIcon className="text-gray-600" size={14} />,
      label: "Rename cluster",
      onClick: () => {
        setSelectedCluster({ id: item.id, name: item.label });
        setRenameClusterModalOpen(true);
      },
    },
    {
      id: "remove-cluster",
      icon: <TrashIcon className="text-red-750" size={14} />,
      label: "Remove cluster",
      onClick: () => {
        setSelectedCluster({ id: item.id, name: item.label });
        setNarrativeClusterDeleteModalOpen(true);
      },
      separator: true,
    },
  ];

  return (
    <MenuPopover placement="right top" offset={0}>
      <Menu>
        {menuItems.map((menuItem, index) => (
          <Fragment key={menuItem.id}>
            <CustomMenuItem config={menuItem} />
            {index === menuItems.length - 2 && (
              <MenuSeparator className="mx-1" />
            )}
          </Fragment>
        ))}
      </Menu>
    </MenuPopover>
  );
};

NarrativeClusterFilterListItemMenu.displayName =
  "NarrativeClusterFilterListItemMenu";

export { NarrativeClusterBulkActionMenu, NarrativeClusterFilterListItemMenu };
